/**
 * Email Detail Component
 * Modern email detail view with threading support
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Avatar,
  Tooltip,
  Paper,
  Stack,
  Alert,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Close as CloseIcon,
  Reply as ReplyIcon,
  ReplyAll as ReplyAllIcon,
  Forward as ForwardIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  AttachFile as AttachFileIcon,
  Download as DownloadIcon,
  Link as LinkIcon,
  Archive as ArchiveIcon,
  Delete as DeleteIcon,
  Assignment as CaseIcon,
  Forum as ThreadIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

import { EmailData } from '../../services/emailService';
import EmailThread from './EmailThread';
import AttachmentList from './AttachmentList';
import CustomerCaseCreationForm from '../cases/customer/CustomerCaseCreationForm';
import CaseSelector from '../common/CaseSelector';
import emailService from '../../services/emailService';
import { CaseData } from '../../services/caseManagementService';

// Styled components with modern design
const DetailContainer = styled(Box)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.palette.background.default
}));

const DetailHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[1]
}));

const DetailContent = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(3),
  overflow: 'auto',
  backgroundColor: theme.palette.background.default
}));

const ActionBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 0),
  borderBottom: `1px solid ${theme.palette.divider}`,
  marginBottom: theme.spacing(2)
}));

const EmailInfo = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(2)
}));

const SenderAvatar = styled(Avatar)(({ theme }) => ({
  width: 48,
  height: 48,
  backgroundColor: theme.palette.primary.main,
  fontSize: '1.1rem',
  fontWeight: 600
}));

interface EmailDetailProps {
  email: EmailData;
  onEmailUpdate: (emailId: string, updates: Partial<EmailData>) => void;
  onClose: () => void;
  onCaseCreated?: (caseData: any) => void;
  onReply?: (emailId: string) => void;
  onForward?: (emailId: string) => void;
}

const EmailDetail: React.FC<EmailDetailProps> = ({
  email,
  onEmailUpdate,
  onClose,
  onCaseCreated,
  onReply,
  onForward
}) => {
  const [threadEmails, setThreadEmails] = useState<EmailData[]>([email]);
  const [loadingThread, setLoadingThread] = useState(false);
  const [showThread, setShowThread] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [selectedCase, setSelectedCase] = useState<CaseData | null>(null);
  const [createCaseDialogOpen, setCreateCaseDialogOpen] = useState(false);

  // Load thread if email has thread_id
  useEffect(() => {
    const loadThread = async () => {
      if (email.thread_id && email.thread_id !== '') {
        setLoadingThread(true);
        try {
          const threadResponse = await emailService.getThread(email.thread_id);
          if (threadResponse && Array.isArray(threadResponse)) {
            setThreadEmails(threadResponse);
            setShowThread(threadResponse.length > 1);
          }
        } catch (error) {
          console.error('Failed to load thread:', error);
          // Fall back to single email
          setThreadEmails([email]);
        } finally {
          setLoadingThread(false);
        }
      }
    };

    loadThread();
  }, [email.thread_id, email.id]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getInitials = (email: string) => {
    const parts = email.split('@')[0].split('.');
    return parts.map(part => part.charAt(0).toUpperCase()).join('').slice(0, 2);
  };

  const handleStarToggle = (emailId: string, starred: boolean) => {
    onEmailUpdate(emailId, { is_starred: starred });
  };

  const handleArchive = () => {
    onEmailUpdate(email.id, { folder: 'archive' });
  };

  const handleDelete = () => {
    onEmailUpdate(email.id, { folder: 'trash' });
  };

  const handleLinkToEntity = () => {
    console.group('🔗 EmailDetail - handleLinkToEntity');
    console.log('📧 Email ID:', email.id);
    console.log('📁 Selected Case:', selectedCase);
    
    if (selectedCase) {
      const updates = {
        linked_entity_type: 'case' as const,
        linked_entity_id: selectedCase.id
      };
      
      console.log('🎯 Updates to send:', updates);
      console.log('🌐 Calling onEmailUpdate...');
      
      onEmailUpdate(email.id, updates);
      
      console.log('✅ onEmailUpdate called successfully');
      setLinkDialogOpen(false);
      setSelectedCase(null);
      console.log('🔄 Dialog closed and case cleared');
    } else {
      console.warn('⚠️ No case selected for linking');
    }
    
    console.groupEnd();
  };

  const handleCaseCreation = (newCase: any) => {
    setCreateCaseDialogOpen(false);
    
    // Link the email to the newly created case
    onEmailUpdate(email.id, {
      linked_entity_type: 'case',
      linked_entity_id: newCase.id
    });
    
    // Notify parent component if callback provided
    if (onCaseCreated) {
      onCaseCreated(newCase);
    }
  };

  const handleRefreshThread = async () => {
    if (email.thread_id) {
      setLoadingThread(true);
      try {
        const threadResponse = await emailService.getThread(email.thread_id);
        if (threadResponse && Array.isArray(threadResponse)) {
          setThreadEmails(threadResponse);
        }
      } catch (error) {
        console.error('Failed to refresh thread:', error);
      } finally {
        setLoadingThread(false);
      }
    }
  };

  return (
    <DetailContainer>
      {/* Header */}
      <DetailHeader>
        <EmailInfo>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <SenderAvatar>
              {getInitials(email.from_address)}
            </SenderAvatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                {email.subject || '(No Subject)'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                From: {email.from_address} • {formatDate(email.email_date)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                To: {email.to_addresses.join(', ')}
              </Typography>
            </Box>
          </Box>
          
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </EmailInfo>

        {/* Action Bar */}
        <ActionBar>
          <Stack direction="row" spacing={1} sx={{ flex: 1 }}>
            <Button
              variant="contained"
              startIcon={<ReplyIcon />}
              onClick={() => onReply?.(email.id)}
              size="small"
            >
              Reply
            </Button>
            <Button
              variant="outlined"
              startIcon={<ForwardIcon />}
              onClick={() => onForward?.(email.id)}
              size="small"
            >
              Forward
            </Button>
            
            {showThread && (
              <Button
                variant="outlined"
                startIcon={loadingThread ? <CircularProgress size={16} /> : <ThreadIcon />}
                onClick={handleRefreshThread}
                disabled={loadingThread}
                size="small"
              >
                Thread ({threadEmails.length})
              </Button>
            )}
          </Stack>

          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Star">
              <IconButton
                size="small"
                onClick={() => handleStarToggle(email.id, !email.is_starred)}
                sx={{ color: email.is_starred ? 'warning.main' : 'action.active' }}
              >
                {email.is_starred ? <StarIcon /> : <StarBorderIcon />}
              </IconButton>
            </Tooltip>
            
            <Tooltip title="Archive">
              <IconButton size="small" onClick={handleArchive}>
                <ArchiveIcon />
              </IconButton>
            </Tooltip>
            
            <Tooltip title="Delete">
              <IconButton size="small" onClick={handleDelete}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </ActionBar>

        {/* Email Status & Labels */}
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <Chip 
            label={email.direction === 'inbound' ? 'Received' : 'Sent'} 
            color={email.direction === 'inbound' ? 'primary' : 'secondary'}
            size="small" 
          />
          <Chip 
            label={email.status} 
            variant="outlined" 
            size="small" 
          />
          <Chip 
            label={email.folder} 
            variant="outlined" 
            size="small" 
          />
          {email.linked_entity_type && email.linked_entity_id && (
            <Chip 
              label={`Linked to ${email.linked_entity_type}: ${email.linked_entity_id}`}
              color="info"
              size="small"
              icon={<LinkIcon />}
            />
          )}
        </Stack>
      </DetailHeader>

      {/* Content */}
      <DetailContent>
        {showThread ? (
          <Box>
            {loadingThread && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Loading conversation thread...
              </Alert>
            )}
            
            <EmailThread
              emails={threadEmails}
              onReply={onReply}
              onForward={onForward}
              onStar={handleStarToggle}
            />
          </Box>
        ) : (
          // Single email view (existing code structure)
          <Box>
            {/* Attachments */}
            <AttachmentList attachments={email.attachments || []} emailId={email.id} />

            {/* Email Body */}
            <Paper sx={{ p: 3, mb: 2 }}>
              {(() => {
                const hasValidHtml = email.body_html && 
                                    email.body_html !== 'false' && 
                                    email.body_html.trim() !== '';
                const hasValidText = email.body_text && 
                                    email.body_text !== 'false' && 
                                    email.body_text.trim() !== '';
                
                if (hasValidHtml) {
                  return (
                    <div 
                      dangerouslySetInnerHTML={{ __html: email.body_html }}
                      style={{ wordBreak: 'break-word', lineHeight: 1.6 }}
                    />
                  );
                } else if (hasValidText) {
                  return (
                    <Typography 
                      variant="body1" 
                      component="div"
                      sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.6 }}
                    >
                      {email.body_text}
                    </Typography>
                  );
                } else {
                  return (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        Email content could not be loaded. This might be due to:
                      </Typography>
                      <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                        <li>Empty email body</li>
                        <li>Unsupported email format</li>
                        <li>Email parsing error</li>
                      </ul>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Debug info: HTML="{String(email.body_html)}", Text="{String(email.body_text)}"
                      </Typography>
                    </Alert>
                  );
                }
              })()}
            </Paper>

            {/* Quick Actions */}
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Quick Actions
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  startIcon={<CaseIcon />}
                  onClick={() => setCreateCaseDialogOpen(true)}
                  size="small"
                >
                  Create Case
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<LinkIcon />}
                  onClick={() => {
                    console.log('🔗 Link to Case button clicked');
                    setLinkDialogOpen(true);
                  }}
                  size="small"
                >
                  Link to Case
                </Button>
              </Stack>
            </Paper>
          </Box>
        )}
      </DetailContent>

      {/* Link to Entity Dialog */}
      <Dialog open={linkDialogOpen} onClose={() => setLinkDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Link Email to Case</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Link this email to an existing case for better organization and tracking.
          </Alert>
          
          <CaseSelector
            value={selectedCase}
            onChange={setSelectedCase}
            placeholder="Search for cases by number, title, or customer..."
            fullWidth
            helperText="Start typing to search for existing cases"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setLinkDialogOpen(false);
            setSelectedCase(null);
          }}>
            Cancel
          </Button>
          <Button 
            onClick={handleLinkToEntity} 
            variant="contained"
            disabled={!selectedCase}
            startIcon={<LinkIcon />}
          >
            Link to Case
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Case Dialog */}
      <CustomerCaseCreationForm 
        open={createCaseDialogOpen}
        onClose={() => setCreateCaseDialogOpen(false)}
        onCaseCreated={handleCaseCreation}
        emailContext={{
          email_id: email.id,
          from_address: email.from_address,
          subject: email.subject,
          body_text: email.body_text,
          attachments: email.attachments || []
        }}
      />
    </DetailContainer>
  );
};

export default EmailDetail;
